import type { FC } from 'react'
import type { PostSummary } from '@/types'

import { ArrowRight } from 'lucide-react'
import { Hashtag } from '@/components/kit/hashtag'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { CoverFallback, PostMeta } from '@/components/blog/post-card'

export const FeaturedPost: FC<{ post: PostSummary }> = ({ post }) => {
    const href = `/journal/${post.slug}`

    return (
        <article className='group grid overflow-hidden rounded-[2rem] border border-black/5 bg-paper shadow-[0_40px_80px_-60px_rgb(30_30_30/0.5)] lg:grid-cols-[1.35fr_1fr] md:rounded-[2.5rem]'>
            <PageLink
                href={href}
                data-cursor='Read'
                tabIndex={-1}
                aria-hidden='true'
                className='relative block aspect-[16/11] overflow-hidden bg-sand lg:aspect-auto lg:min-h-[520px]'
            >
                {post.cover ? (
                    <Photo
                        src={post.cover}
                        alt={post.cover_alt ?? ''}
                        sizes='(min-width: 1024px) 60vw, 100vw'
                        preload
                        grain
                        className='absolute inset-0'
                        imgClassName='transition-transform duration-[1.4s] ease-expo group-hover:scale-105'
                    />
                ) : (
                    <CoverFallback title={post.title} />
                )}

                <span className='absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-ember px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ink md:left-7 md:top-7'>
                    Featured
                </span>
            </PageLink>

            <div className='flex flex-col p-7 md:p-10 lg:p-12'>
                <PostMeta post={post} />

                <h2 className='mt-5 text-[clamp(2.1rem,3.8vw,3.5rem)] font-bold leading-[0.98] tracking-[-0.035em]'>
                    <PageLink
                        href={href}
                        className='bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_3px] bg-left-bottom bg-no-repeat transition-[background-size] duration-700 ease-expo group-hover:bg-[length:100%_3px]'
                    >
                        <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                    </PageLink>
                </h2>

                {post.excerpt && (
                    <p
                        className='mt-5 text-lg leading-relaxed text-stone'
                        data-mdlfy-cms={`posts:${post.id}:excerpt`}
                    >
                        {post.excerpt}
                    </p>
                )}

                {post.hashtags.length > 0 && (
                    <div className='mt-6 flex flex-wrap gap-2'>
                        {post.hashtags.map(tag => (
                            <Hashtag
                                key={tag}
                                tag={tag}
                                tone='outline'
                            />
                        ))}
                    </div>
                )}

                <div className='mt-auto flex flex-wrap items-center justify-between gap-6 pt-10'>
                    {post.author && (
                        <div className='flex items-center gap-3'>
                            {post.author.avatar && (
                                <Photo
                                    src={post.author.avatar}
                                    alt={post.author.avatar_alt ?? ''}
                                    sizes='48px'
                                    position='50% 25%'
                                    className='size-12 rounded-full'
                                />
                            )}
                            <div>
                                <p
                                    className='font-semibold'
                                    data-mdlfy-cms={`authors:${post.author.id}:name`}
                                >
                                    {post.author.name}
                                </p>
                                {post.author.role && (
                                    <p
                                        className='text-sm text-stone'
                                        data-mdlfy-cms={`authors:${post.author.id}:role`}
                                    >
                                        {post.author.role}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <PageLink
                        href={href}
                        className='inline-flex items-center gap-3 rounded-full bg-ink py-2 pl-5 pr-2 text-sm font-medium text-cream transition-colors hover:bg-black'
                    >
                        Read the cover story
                        <span className='flex size-8 items-center justify-center rounded-full bg-ember text-ink'>
                            <ArrowRight className='size-4 transition-transform duration-500 ease-expo group-hover:translate-x-0.5' />
                        </span>
                    </PageLink>
                </div>
            </div>
        </article>
    )
}
